import axios from 'axios';

import { getAuthToken, isTokenValid } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180';

// API interfaces
export interface OrderListRequest {
  orderType: string;
  customerCode?: string;
  vendorCode?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: string;
  storeCode?: string;
  warehouseCode?: string;
  orderNumber?: string;
}

export interface OrderListItem {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  customerName: string;
  totalAmount: number;
  currency: string;
  status: string;
  orderType: string;
  description?: string;
}

export interface OrderListResult {
  items: OrderListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface OrderHeader {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  customerName: string;
  customerAddress: string;
  totalAmount: number;
  currency: string;
  status: string;
  orderType: string;
  description?: string;
  createdBy: string;
  createdDate: string;
  lastUpdatedBy?: string;
  lastUpdatedDate?: string;
}

export interface OrderLine {
  lineNumber: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  unit: string;
  description?: string;
  // Eksik özellikleri ekleyin
  taxRate?: number;
  discountRate?: number;
  netAmount?: number;
  taxAmount?: number;
}

export interface OrderDetailResult {
  header: OrderHeader;
  lines: OrderLine[];
}

export interface OrderStatisticsRequest {
  orderType: string;
  startDate?: string;
  endDate?: string;
}

export interface OrderStatistics {
  totalOrders: number;
  pendingOrders: number;
  approvedOrders: number;
  totalAmount: number;
  currency: string;
}

export interface CreateOrderRequest {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  status: string;
  orderType: string;
  description?: string;
  lines: {
    lineNumber: number;
    itemCode: string;
    quantity: number;
    unitPrice: number;
    description?: string;
  }[];
}

export interface UpdateOrderStatusRequest {
  orderNumber: string;
  status: string;
  notes?: string;
}

export interface GenerateOrderNumberResult {
  orderNumber: string;
}

// Wholesale Order interfaces - Invoice API'den uyarlandı
export interface CreateWholesaleOrderRequest {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  currencyCode: string;
  exchangeRate: number;
  paymentType: string;
  seriesCode: string;
  serialNumber: string;
  warehouseCode: string;
  description?: string;
  orderLines: CreateOrderLineRequest[];
}

export interface CreateOrderLineRequest {
  itemCode: string;
  description?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountRate: number;
  vatRate: number;
}

// Create axios instance with auth and cache busting
const createAuthenticatedRequest = () => {
  const token = getAuthToken();
  
  // Token kontrolü
  if (!token) {
    console.error('❌ Token bulunamadı! Kullanıcı oturum açmamış olabilir.');
    throw new Error('Authentication token not found');
  }
  
  if (!isTokenValid()) {
    console.error('❌ Token geçersiz! Kullanıcının tekrar oturum açması gerekiyor.');
    throw new Error('Authentication token is invalid or expired');
  }
  
  console.log('🔑 Token bulundu ve geçerli:', token.substring(0, 20) + '...');
  
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
    // Cache busting için timestamp ekle
    params: {
      _t: Date.now()
    }
  });
};

export const orderService = {
  // Get orders list - Backend'de GET metoduyla tanımlanmış
  async getOrders(request: OrderListRequest): Promise<OrderListResult> {
    try {
      const api = createAuthenticatedRequest();
      
      // Kullanıcı bilgisini localStorage'dan al
      const userJson = localStorage.getItem('user');
      let userId = null;
      let userRoles: string[] = [];
      
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          userId = userData.id;
          userRoles = userData.roles || [];
          console.log('OrderService: User roles from localStorage:', userRoles);
        } catch (e) {
          console.error('OrderService: Error parsing user data from localStorage', e);
        }
      }
      
      // OrderType'a göre doğru endpoint'i seç - BACKEND'DEKİ DOĞRU ENDPOINT'LER
      const endpoint = request.orderType === 'sales' ? '/api/v1/orders/sales' : '/api/v1/orders/purchase';
      
      // Backend'in beklediği parametre isimlerini kullan
      const params: { [key: string]: string | number | null } = {
        customerCode: request.customerCode || null,
        vendorCode: request.vendorCode || null,
        startDate: request.startDate || null,
        endDate: request.endDate || null,
        status: request.status || null,
        pageNumber: request.page, // Backend'de pageNumber bekleniyor
        pageSize: request.pageSize,
        sortBy: request.sortBy || null,
        sortDirection: request.sortDirection || null,
        storeCode: request.storeCode || null,
        warehouseCode: request.warehouseCode || null,
      };
      
      // Eğer kullanıcı satış elemanı ise ve admin değilse, sadece kendi müşterilerine ait siparişleri göster
      if (userId && userRoles.includes('sales') && !userRoles.includes('admin') && request.orderType === 'sales') {
        console.log('OrderService: Sales user detected, filtering orders by sales person');
        // Satış elemanı filtresi ekle
        params.salesPersonId = userId;
      }
      
      params.orderNumber = request.orderNumber || null;
      params._cacheBust = Date.now(); // Cache busting
      
      // Boş parametreleri temizle
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === '') {
          delete params[key];
        }
      });
      
      console.log('🚀 API çağrısı yapılıyor (GET):', endpoint, params);
      
      const response = await api.get(endpoint, { params });
      console.log('✅ API yanıtı alındı:', response.status);
      return response.data;
    } catch (error: any) {
      console.error('❌ API hatası:', error);
      
      // 401 hatası özel olarak ele al
      if (error.response?.status === 401) {
        console.error('❌ 401 Unauthorized: Token geçersiz veya süresi dolmuş');
        // Token'ı temizle ve kullanıcıyı login sayfasına yönlendir
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      throw error;
    }
  },

  // Get order detail
  async getOrderDetail(orderNumber: string): Promise<OrderDetailResult> {
    const api = createAuthenticatedRequest();
    const response = await api.get(`/api/v1/orders/${orderNumber}`);
    return response.data;
  },

  // Get order statistics - Backend'de GET metoduyla tanımlanmış
  async getOrderStatistics(request: OrderStatisticsRequest): Promise<OrderStatistics> {
    const api = createAuthenticatedRequest();
    const params = {
      orderType: request.orderType,
      startDate: request.startDate || '',
      endDate: request.endDate || '',
      _cacheBust: Date.now()
    };
    const response = await api.get('/api/v1/orders/statistics', { params });
    return response.data;
  },

  // Create new order
  async createOrder(request: CreateOrderRequest): Promise<{ orderNumber: string }> {
    const api = createAuthenticatedRequest();
    const response = await api.post('/api/v1/orders', request);
    return response.data;
  },

  // Update order
  async updateOrder(orderNumber: string, request: CreateOrderRequest): Promise<void> {
    const api = createAuthenticatedRequest();
    await api.put(`/api/v1/orders/${orderNumber}`, request);
  },

  // Delete order
  async deleteOrder(orderNumber: string): Promise<void> {
    const api = createAuthenticatedRequest();
    await api.delete(`/api/v1/orders/${orderNumber}`);
  },

  // Update order status - Backend'de PUT metoduyla tanımlanmış
  async updateOrderStatus(request: UpdateOrderStatusRequest): Promise<void> {
    const api = createAuthenticatedRequest();
    await api.put(`/api/v1/orders/${request.orderNumber}/status`, {
      status: request.status,
      notes: request.notes
    });
  },

  // Generate order number - Backend'de GET metoduyla tanımlanmış
  async generateOrderNumber(orderType: string): Promise<GenerateOrderNumberResult> {
    const api = createAuthenticatedRequest();
    const processCode = orderType === 'sales' ? 'SO' : 'PO';
    const response = await api.get('/api/v1/orders/generate-number', { 
      params: { 
        processCode,
        _cacheBust: Date.now()
      } 
    });
    return response.data;
  },

  // Create wholesale order - Invoice API'den uyarlandı
  async createWholesaleOrder(request: CreateWholesaleOrderRequest): Promise<{ orderNumber: string }> {
    const api = createAuthenticatedRequest();
    console.log('🚀 Wholesale order oluşturuluyor:', request);
    const response = await api.post('/api/v1/orders/wholesale', request);
    console.log('✅ Wholesale order oluşturuldu:', response.data);
    return response.data;
  }
};

// Cache busting için service'i yeniden export et
export default orderService;